import { buildQueryParams, downloadFile } from 'utils/helpers';
import HTTPClient, { HTTPClientParameters } from './HTTPClient';
import cache from './cache';
import { YoYRevenue, RevenueByMonth, RevenueReportByRentalType, PacingReport } from 'types/TransactionReport';

type TransactionReportsClientServiceParams = HTTPClientParameters;

const tenMinutesInMilliseconds = 10 * 60000;

class TransactionReportsClient extends HTTPClient {
    constructor(params: TransactionReportsClientServiceParams) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    _getFile(url: string, fileName: string): Promise<void> {
        return this.getRaw(url)
            .then((response) => response.blob())
            .then((blob) => {
                downloadFile({
                    data: blob,
                    fileName,
                    fileType: 'csv',
                });
            });
    }

    @cache(tenMinutesInMilliseconds)
    getRevenueMoMReport(spotUuid: string, startDate?: string, endDate?: string): Promise<RevenueByMonth> {
        return this.get(
            `transactions/reports/revenue/mom${buildQueryParams({
                spotUuid,
                startDate,
                endDate,
            })}`
        );
    }
    getRevenueMoMReportCSV(spotUuid: string, fileName: string, startDate?: string): Promise<void> {
        let url = `transactions/reports/revenue/mom?spot_uuid=${spotUuid}&format=csv`;
        if (startDate) url += `&start_date=${startDate}`;
        return this._getFile(url, fileName);
    }

    @cache(tenMinutesInMilliseconds)
    getRevenueDailyReport(spotUuid: string, startDate: string): Promise<RevenueByMonth> {
        return this.get(
            `transactions/reports/revenue/dad${buildQueryParams({
                spotUuid,
                startDate,
            })}`
        );
    }
    getRevenueDailyReportCSV(spotUuid: string, fileName: string, startDate: string): Promise<void> {
        return this._getFile(
            `transactions/reports/revenue/dad${buildQueryParams({
                spotUuid,
                startDate,
                format: 'csv',
            })}`,
            fileName
        );
    }

    @cache(tenMinutesInMilliseconds)
    getRevenueByRentalType(spotUuid: string, rentalType?: string): Promise<RevenueReportByRentalType> {
        return this.get(
            `transactions/reports/revenue/mom/rental_types${buildQueryParams({
                spotUuid,
                rentalType,
            })}`
        );
    }
    getRevenueByRentalTypeCSV(spotUuid: string, rentalTypes: string, fileName: string): Promise<void> {
        let url = `transactions/reports/revenue/mom/rental_types?spot_uuid=${spotUuid}&format=csv`;
        if (rentalTypes) {
            for (const rt of rentalTypes.split(',')) url += `&rental_type=${rt}`;
        }
        return this._getFile(url, fileName);
    }

    @cache(tenMinutesInMilliseconds)
    getYoYRevenueReport(spotUuid: string, month: string): Promise<YoYRevenue> {
        return this.get(`transactions/reports/revenue/yoy?spot_uuid=${spotUuid}&month=${month}`);
    }

    getYoYRevenueReportCSV(spotUuid: string, month: string, fileName: string): Promise<void> {
        return this._getFile(
            `transactions/reports/revenue/yoy?spot_uuid=${spotUuid}&month=${month}&format=csv`,
            fileName
        );
    }

    @cache(tenMinutesInMilliseconds)
    getPacingReport(spotUuid: string): Promise<PacingReport> {
        return this.get(`transactions/reports/revenue/pacing?spot_uuid=${spotUuid}`);
    }

    getPacingReportCSV(spotUuid: string, fileName: string): Promise<void> {
        return this._getFile(`transactions/reports/revenue/pacing?spot_uuid=${spotUuid}&format=csv`, fileName);
    }

    getRevenueExcelReport(
        spotUuid: string,
        fileName: string,
        yoyMonth: string,
        rentalTypes: string,
        dadStartDate: string,
        momStartDate?: string
    ): Promise<void> {
        let url = `transactions/reports/revenue/excel${buildQueryParams({
            spotUuid,
            yoyMonth,
            dadStartDate,
            momStartDate,
        })}`;
        if (rentalTypes) {
            for (const rt of rentalTypes.split(',')) url += `&rental_type=${rt}`;
        }
        return this._getFile(url, fileName);
    }
}

export default TransactionReportsClient;
